import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { faqPageSchema, type Faq } from "@/data/serviceFaqs";
import heroImg from "@/assets/whole-home-audio.webp";

const hero = heroImg;
const URL = "https://apexav.com/whole-home-audio-installation-near-me";

/** Nearby neighborhoods and suburbs, each with copy written for that area only. */
const NEIGHBORHOODS: { name: string; blurb: string; to: string; params?: Record<string, string> }[] = [
  {
    name: "Cherry Creek",
    blurb:
      "Condos and townhomes with shared walls need quiet zones, not just loud ones. We size in-ceiling speakers for even coverage at conversation volume and keep subwoofers off party walls entirely.",
    to: "/cherry-creek/smart-home-installation",
  },
  {
    name: "Highlands Ranch",
    blurb:
      "Open main floors with twenty-foot great rooms are the hardest rooms to fill evenly. We zone the kitchen, great room, and patio separately so one source doesn't blast the whole floor.",
    to: "/highlands-ranch/smart-home-installation",
  },
  {
    name: "Castle Rock",
    blurb:
      "Walk-out patios and decks get as much use as the living room nine months a year. Weather-rated outdoor speakers on their own zone mean the game follows you outside without dragging a speaker around.",
    to: "/castle-rock/smart-home-installation",
  },
  {
    name: "Castle Pines",
    blurb:
      "Larger floor plans with formal and casual spaces. We design zone maps around how the house is actually used — quiet background in the study, full range in the gym, nothing bleeding into bedrooms.",
    to: "/castle-pines/smart-home-installation",
  },
  {
    name: "Parker",
    blurb:
      "Newer construction where prewire is often still on the table. Catching a Parker build at framing is the cheapest time to run speaker wire to every room, even if you only finish a few zones now.",
    to: "/service-areas/$slug",
    params: { slug: "parker" },
  },
  {
    name: "Littleton",
    blurb:
      "Older ranches with finished basements and low ceilings. In-ceiling speakers keep the floor plan clean where surface-mount speakers would crowd the room.",
    to: "/service-areas/$slug",
    params: { slug: "littleton" },
  },
  {
    name: "Boulder",
    blurb:
      "A lot of Boulder homes already have a good two-channel system. We integrate it as one zone of the whole-house system instead of replacing gear you already like.",
    to: "/service-areas/$slug",
    params: { slug: "boulder" },
  },
  {
    name: "Centennial",
    blurb:
      "Two-story family homes where the request is usually the same: music in the kitchen and patio, quiet everywhere else. Simple zone control from a phone or a wall keypad, no app maze.",
    to: "/service-areas/$slug",
    params: { slug: "centennial" },
  },
];

const faqs: Faq[] = [
  {
    q: "Who installs whole-home audio near me in the Denver area?",
    a: "Apex AV designs and installs whole-home audio systems throughout the Denver metro and Front Range from our shop at 2921 W 38th Ave in Denver. We regularly work in Cherry Creek, Hilltop, Greenwood Village, Cherry Hills Village, Highlands Ranch, Lone Tree, Castle Pines, Castle Rock, Parker, Centennial, Littleton, Arvada, Golden, and Boulder. The crew that quotes your home is the crew that installs it.",
  },
  {
    q: "How much does whole-home audio installation cost near Denver?",
    a: "A four-zone system with in-ceiling speakers, a centralized amplifier, and phone or keypad control typically runs $8,000 to $15,000 installed. Larger homes with eight or more zones, outdoor coverage, and invisible speakers usually land between $15,000 and $40,000. Every quote is fixed-bid after a free in-home walkthrough.",
  },
  {
    q: "Can you add whole-home audio to an existing finished house?",
    a: "Yes — retrofit is most of our work. We fish speaker wire through finished walls and ceilings from the attic or basement, cut in-ceiling speakers with minimal drywall work, and patch and texture so the finish matches. If a path genuinely can't be fished, we redesign the zone around it rather than leaving exposed raceway.",
  },
  {
    q: "Do I need speakers in every room?",
    a: "No. Most Denver homes start with four to six zones: kitchen, great room, primary suite, office, patio, and maybe the gym. We wire for the rooms you'll actually use and leave conduit or spare wire where a future zone makes sense.",
  },
  {
    q: "Can I play different music in different rooms?",
    a: "Yes. Every zone is independent — jazz in the kitchen, a podcast in the office, the game on the patio — all from one app, wall keypads, or voice. Grouping zones for a party is one tap.",
  },
  {
    q: "Will the speakers be visible?",
    a: "Only as much as you want. Standard in-ceiling speakers have paintable magnetic grilles that sit nearly flush. For a truly invisible finish we install speakers behind the drywall surface itself — you hear them, you never see them.",
  },
  {
    q: "Do you come back after the install?",
    a: "Yes. We return for post-install tuning once you've lived with the system, and we support it long-term. Being local is the whole point — there is no ticket queue in another state.",
  },
];

export const Route = createFileRoute("/whole-home-audio-installation-near-me")({
  head: () => ({
    meta: [
      { title: "Whole-Home Audio Installation Near Me | Denver & Front Range — Apex AV" },
      {
        name: "description",
        content:
          "Looking for whole-home audio installation near you? Apex AV designs and installs multi-room audio across Denver, Cherry Creek, Highlands Ranch, Castle Rock, Parker and the Front Range. Free in-home consultation.",
      },
      {
        name: "keywords",
        content:
          "home audio installation near me, whole home audio installation near me, multi room audio installers near me, whole house audio Denver, in ceiling speaker installation near me",
      },
      { property: "og:title", content: "Whole-Home Audio Installation Near Me — Apex AV Denver" },
      {
        property: "og:description",
        content:
          "Local Denver-area whole-home audio: multi-room zones, in-ceiling and invisible speakers, outdoor audio, and on-site tuning.",
      },
      { property: "og:image", content: `https://apexav.com${hero}` },
      { property: "og:image:alt", content: "Whole-home audio installation near me — Apex AV Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Whole-Home Audio Installation Near Me — Apex AV Denver" },
      { name: "twitter:description", content: "Denver-area multi-room audio design, installation, and tuning." },
      { name: "twitter:image", content: `https://apexav.com${hero}` },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            faqPageSchema(faqs, URL),
            {
              "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
              "@id": `${URL}#localbusiness`,
              name: "Apex AV — Whole-Home Audio Installation Near Me",
              image: `https://apexav.com${hero}`,
              logo: "https://apexav.com/logo.png",
              url: URL,
              telephone: "+1-303-317-3233",
              email: "info@apexav.com",
              priceRange: "$$$",
              address: {
                "@type": "PostalAddress",
                streetAddress: "2921 W 38th AVE STE 169",
                addressLocality: "Denver",
                addressRegion: "CO",
                postalCode: "80211",
                addressCountry: "US",
              },
              geo: { "@type": "GeoCoordinates", latitude: 39.7714, longitude: -105.0234 },
              areaServed: NEIGHBORHOODS.map((n) => ({ "@type": "City", name: `${n.name}, CO` })),
              openingHoursSpecification: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                  opens: "08:00",
                  closes: "18:00",
                },
                { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "09:00", closes: "16:00" },
              ],
              parentOrganization: { "@id": "https://apexav.com/#organization" },
            },
            {
              "@type": "Service",
              name: "Whole-Home Audio Installation",
              description:
                "Multi-room and whole-home audio design, installation, and tuning for homes across the Denver metro and Front Range.",
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "AdministrativeArea", name: "Denver Metro & Front Range, Colorado" },
              serviceType: "Whole-Home Audio Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "8000" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://apexav.com/" },
                { "@type": "ListItem", position: 2, name: "Whole-Home Audio Installation Near Me", item: URL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: WholeHomeAudioInstallationNearMe,
});

function WholeHomeAudioInstallationNearMe() {
  return (
    <>
      <ServiceDetail
        eyebrow="Whole-home audio installation near me"
        title="Whole-home audio installation near you, across Denver and the Front Range."
        intro={
          <>
            If you searched <strong>“home audio installation near me”</strong> from a Denver-area address, you're in
            our service radius. Apex AV designs and installs multi-room audio systems from Cherry Creek to Castle
            Pines — measured in person, quoted fixed-bid, and supported by the same local team afterward.
          </>
        }
        image={hero}
        imageAlt="Whole-home audio installation near me in Denver, Colorado — in-ceiling speakers in a finished living space"
        startingPrice="$8,000"
        priceNote="Four-zone systems from $8K. Larger homes typically $15K–$40K."
        bullets={[
          "Free in-home walkthrough and zone plan",
          "Fixed-bid pricing before anyone cuts a hole",
          "Independent zones — different music in every room",
          "In-ceiling, in-wall, and truly invisible speaker options",
          "Weather-rated outdoor zones for patios and decks",
          "Retrofit wire fishing with patch, texture, and paint",
          "Control from one app, wall keypads, or voice",
          "Local follow-up visits, not a remote support queue",
        ]}
        features={[
          { title: "Booked in days", desc: "Denver-metro consultations are usually scheduled within five business days." },
          { title: "Zoned for real life", desc: "We map zones to how you actually use the house, not a template." },
          { title: "Tuned on site", desc: "Every zone is leveled and EQ'd in person, then revisited once you settle in." },
        ]}
        faqs={faqs}
        relatedServices={[
          { to: "/services/whole-home-audio", label: "Whole-Home Audio", desc: "The full service page: zones, gear, and what a system includes." },
          { to: "/services/hidden-audio", label: "Hidden & Invisible Audio", desc: "Speakers that disappear into the wall or ceiling." },
          { to: "/services/home-theater", label: "Home Theater Design & Install", desc: "Dedicated theaters and media rooms for the serious room." },
        ]}
      />

      <section className="border-t border-border bg-card/30">
        <div className="container mx-auto px-6 py-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Nearby neighborhoods we install in</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Construction differs street to street around Denver — shared walls in Cherry Creek, open great rooms in
            Highlands Ranch, walk-out patios in Castle Rock. Here's what changes the design in each area.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {NEIGHBORHOODS.map((n) => (
              <Link
                key={n.name}
                to={n.to}
                params={n.params as never}
                className="card-elegant sheen group rounded-xl border border-border bg-card p-5 shadow-card transition hover:border-brand/50 hover:shadow-glow"
              >
                <h3 className="font-display text-lg font-semibold group-hover:text-brand-text">{n.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{n.blurb}</p>
                <span className="mt-3 inline-block text-sm text-brand-text">View area →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-16 border-t border-border">
        <h2 className="font-display text-2xl md:text-3xl font-bold">Pair it with the room that matters most</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Most whole-home audio projects include one room that gets the full treatment. Our{" "}
          <Link
            to="/blog/custom-home-theater-design-guide"
            className="font-semibold text-brand-text underline underline-offset-4"
          >
            custom home theater design guide
          </Link>{" "}
          walks through every decision on a Denver theater build, in the order it matters.
        </p>
        <Link
          to="/blog/custom-home-theater-design-guide"
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-brand/50 transition"
        >
          Read the custom home theater design guide →
        </Link>
      </section>
    </>
  );
}
